import { type MutableRefObject, useEffect, useRef } from "react";

type ObserverStatus = "observe" | "unobserve";
export type HandleInViewportChange = (
  isIntersecting: boolean,
) => Promise<ObserverStatus> | ObserverStatus;

type InViewportWrapperProps = {
  ref: MutableRefObject<HTMLElement | null>;
  handleInViewportChange: HandleInViewportChange;
  threshold?: number;
  baseOffset?: number;
};

/**
 * @param ref The element to observe.
 * @param handleInViewportChange This function should be wrapped in a useCallback to prevent rerenders. It should return "observe" or "unobserve" (either as a string or asynchronously via a promise) to continue or stop observing it.
 * @param threshold The minimum amount of the element that must be visible in the viewport, from 0-1.
 * @param baseOffset A value for margin bottom to offset the base root margin.
 */

export function useIsInViewport({
  ref,
  handleInViewportChange,
  threshold = 0,
  baseOffset = 0,
}: InViewportWrapperProps) {
  const observerStatus = useRef<ObserverStatus>("observe");

  useEffect(() => {
    // If we don't have access to IntersectionObserver in older browsers or tests,
    // or if `stopObservingRef` is set to true we don't want to start a new observer.
    if (
      !("IntersectionObserver" in window) ||
      !("IntersectionObserverEntry" in window) ||
      !("intersectionRatio" in window.IntersectionObserverEntry.prototype) ||
      observerStatus.current === "unobserve"
    ) {
      return;
    }

    const rootMargin = `0px 0px -${baseOffset}px 0px`;

    const observer = new IntersectionObserver(
      async ([entry]) => {
        const status = handleInViewportChange(entry.isIntersecting);

        Promise.resolve(status).then((resolvedStatus) => {
          observerStatus.current = resolvedStatus;

          if (status === "unobserve") {
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold,
        rootMargin,
      },
    );

    if (ref.current && observerStatus.current === "observe") {
      observer.observe(ref.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [ref, threshold, baseOffset, handleInViewportChange]);
}
